import { JYOTISHA_MARGA_TAB_GROUPS } from './jyotishaMargaTabGroups';

/** Canonical chapter order (matches Jyotisha home `TAB_GROUPS`). */
export const JYOTISHA_CHAPTER_ORDER = [
  'introduction',
  'bhava-cosmos',
  'rashis',
  'nakshatra',
  'planets',
  'panchang',
  'calculators',
  'reading-charts',
  'rituals',
  'test-knowledge',
  'my-observations',
  'phaladeepika',
  'jyotisha-stories',
];

/**
 * @param {Array<{ id: string, tabs: Array<{ id: string }> }>} groups
 * @returns {Record<string, string>}
 */
/** Number all Jyotisha chapters in canonical order. Saar continues from 7 onward. */
export function buildLessonNumberMaps(groups) {
  const orderMap = Object.fromEntries(JYOTISHA_CHAPTER_ORDER.map((id, index) => [id, index]));
  const sorted = [...groups].sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));
  const byTabId = {};
  sorted.forEach((group, chapterIndex) => {
    group.tabs.forEach((tab, lessonIndex) => {
      byTabId[tab.id] = `${chapterIndex + 1}.${lessonIndex + 1}`;
    });
  });
  return byTabId;
}

export const LESSON_NUMBER_BY_TAB_ID = buildLessonNumberMaps(JYOTISHA_MARGA_TAB_GROUPS);
export const CHAPTER_NUMBER_BY_GROUP_ID = Object.fromEntries(
  JYOTISHA_CHAPTER_ORDER.map((groupId, index) => [groupId, String(index + 1)])
);

function sortGroupsByChapterOrder(groups) {
  const orderMap = Object.fromEntries(JYOTISHA_CHAPTER_ORDER.map((id, index) => [id, index]));
  return [...groups].sort((a, b) => (orderMap[a.id] ?? 999) - (orderMap[b.id] ?? 999));
}

/** Flat syllabus tab ids in chapter.lesson order (home + Marga). */
export const SYLLABUS_TAB_IDS = sortGroupsByChapterOrder(JYOTISHA_MARGA_TAB_GROUPS).flatMap((group) =>
  group.tabs.map((tab) => tab.id)
);

/**
 * Previous / next lesson within the numbered syllabus only.
 * @param {string | null | undefined} tabId
 * @returns {{ previousId: string | null, nextId: string | null, index: number }}
 */
export function getSyllabusLessonNav(tabId) {
  if (!tabId) return { previousId: null, nextId: null, index: -1 };
  const index = SYLLABUS_TAB_IDS.indexOf(tabId);
  if (index < 0) return { previousId: null, nextId: null, index: -1 };
  return {
    previousId: index > 0 ? SYLLABUS_TAB_IDS[index - 1] : null,
    nextId: index < SYLLABUS_TAB_IDS.length - 1 ? SYLLABUS_TAB_IDS[index + 1] : null,
    index,
  };
}

/**
 * Compare two syllabus lessons. Returns -1 if A is earlier, 1 if later, 0 if same, null if unknown.
 * @param {string} tabIdA
 * @param {string} tabIdB
 * @returns {-1 | 0 | 1 | null}
 */
export function compareLessonOrder(tabIdA, tabIdB) {
  const idxA = SYLLABUS_TAB_IDS.indexOf(tabIdA);
  const idxB = SYLLABUS_TAB_IDS.indexOf(tabIdB);
  if (idxA < 0 || idxB < 0) return null;
  if (idxA < idxB) return -1;
  if (idxA > idxB) return 1;
  return 0;
}

/** @param {string} tabId */
export function getLessonNumber(tabId) {
  return LESSON_NUMBER_BY_TAB_ID[tabId] ?? null;
}

/**
 * Chapter group labels for a syllabus tab (e.g. प्रारंभ / Prarambh).
 * Returns null for Phaladeepika, lagna-baseline, and quiz routes (outside numbered syllabus).
 * @param {string} tabId
 * @returns {{ groupHi: string, groupEn: string } | null}
 */
export function getSyllabusGroupForTopic(tabId) {
  if (!tabId) return null;
  for (const group of JYOTISHA_MARGA_TAB_GROUPS) {
    if (group.tabs.some((tab) => tab.id === tabId)) {
      return { groupHi: group.label, groupEn: group.labelEn };
    }
  }
  return null;
}

/** Tab ids in the numbered syllabus that lack a lesson number (should always be empty). */
export function listSyllabusTabsMissingLessonNumbers() {
  return SYLLABUS_TAB_IDS.filter((tabId) => !LESSON_NUMBER_BY_TAB_ID[tabId]);
}

/** @param {string} groupId */
export function getChapterNumber(groupId) {
  return CHAPTER_NUMBER_BY_GROUP_ID[groupId] ?? null;
}

const DEVANAGARI_DIGITS = '०१२३४५६७८९';

/**
 * Drop syllabus number already baked into marga labels (e.g. `3.3 ·` or `३.३ ·`).
 * @param {string} label
 * @param {string} tabId
 */
export function stripLeadingLessonNumber(label, tabId) {
  const num = getLessonNumber(tabId);
  if (!num || !label) return label;
  const escaped = num.replace(/\./g, '\\.');
  let stripped = label.replace(new RegExp(`^${escaped}\\s*[·•.]?\\s*`, 'i'), '').trim();
  const hiNum = String(num).replace(/[0-9]/g, (d) => DEVANAGARI_DIGITS[Number(d)]);
  stripped = stripped.replace(new RegExp(`^${hiNum}\\s*[·•.]?\\s*`), '').trim();
  return stripped;
}

/** @param {string} tabId @param {string} label */
export function formatLessonWithNumber(tabId, label) {
  const num = getLessonNumber(tabId);
  return num ? `${num} · ${label}` : label;
}

/** Syllabus title with one lesson number (strips duplicate prefix in `label`). */
export function formatLessonTitle(tabId, label) {
  return formatLessonWithNumber(tabId, stripLeadingLessonNumber(label, tabId));
}
